'use client';

import { useState } from 'react';
import { KeyRound, LogOut } from 'lucide-react';
import { SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import { ApiError } from '@/lib/api';
import { signOut } from '@/lib/campus';
import { useProfile } from './ProfileContext';
import { Form, TextField } from './forms';
import { useToast, CTA_DANGER, InlineError, ProfileSheet } from './ui';

/**
 * The end of the page: Sign out as the one loud, red action, and Change
 * password as the quiet text link under it. No card around either; these
 * are chores, not content.
 */
export function AccountSection() {
  const { mock } = useProfile();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const doSignOut = async () => {
    setSigningOut(true);
    if (mock) {
      window.location.assign(window.location.pathname + '?mock=1&signedout=1');
      return;
    }
    await signOut();
  };

  return (
    <section className="flex flex-col items-center gap-4 pt-4 pb-2">
      <button
        type="button"
        onClick={doSignOut}
        disabled={signingOut}
        className={`${CTA_DANGER} w-full max-w-xs`}
      >
        <LogOut className="h-4 w-4" strokeWidth={2.5} />
        {signingOut ? 'Signing out…' : 'Sign out'}
      </button>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 transition-colors hover:text-[#1c2b39] hover:underline"
      >
        <KeyRound className="h-3.5 w-3.5" strokeWidth={2.5} />
        Change password
      </button>

      <ProfileSheet
        open={open}
        onClose={() => setOpen(false)}
        title="Change password"
        maxWidth="max-w-lg"
      >
        <SheetPage name="main">
          <PasswordForm onDone={() => setOpen(false)} />
        </SheetPage>
      </ProfileSheet>
    </section>
  );
}

const MIN = 8;

export function PasswordForm({ onDone }: { onDone: () => void }) {
  const { api } = useProfile();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = next.length > 0 && next.length < MIN;
  const mismatch = confirm.length > 0 && confirm !== next;
  const valid = current.length > 0 && next.length >= MIN && confirm === next;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.changePassword(current, next);
      toast('Password changed', 'ok');
      onDone();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Could not change your password. Try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Form
      onSubmit={() => void submit()}
      isValid={valid}
      isSubmitting={busy}
      submitLabel="Change password"
      helperText={`At least ${MIN} characters.`}
    >
      <TextField
        label="Current password"
        value={current}
        onChange={setCurrent}
        type="password"
        autoComplete="current-password"
        required
        autoFocus
      />
      <TextField
        label="New password"
        value={next}
        onChange={setNext}
        type="password"
        autoComplete="new-password"
        required
        help={
          tooShort ? (
            <span className="text-red-600">Needs at least {MIN} characters.</span>
          ) : undefined
        }
      />
      <TextField
        label="Confirm new password"
        value={confirm}
        onChange={setConfirm}
        type="password"
        autoComplete="new-password"
        required
        help={mismatch ? <span className="text-red-600">These don't match yet.</span> : undefined}
      />
      <InlineError message={error} />
    </Form>
  );
}
